import React, { useState, useCallback, useImperativeHandle, forwardRef, useMemo } from 'react';
import { ImageWithPlaceholder } from './Placeholders';
import { apiService } from '../auth';

// Service pour l'upload vers Cloudinary via notre API backend
class CloudinaryUploadService {
  static async uploadViaBackend(file, options = {}) {
    try {
      const formData = new FormData();
      formData.append('files', file);
      
      if (options.folder) {
        formData.append('folder', options.folder);
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/upload/images`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `Upload failed: ${response.statusText}`);
      }

      const result = await response.json();
      return {
        success: result.success,
        data: result.images[0] // Retourner la première image uploadée
      };
    } catch (error) {
      console.error('Erreur upload via backend:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  static async uploadDirect(file, options = {}) {
    // Utiliser l'API backend au lieu de l'upload direct
    return this.uploadViaBackend(file, options);
  }

  static generateOptimizedUrl(publicId, options = {}) {
    const {
      width,
      height,
      crop = 'fill',
      quality = 'auto',
      format = 'auto'
    } = options;

    let transformations = [`f_${format}`, `q_${quality}`];
    
    if (width) transformations.push(`w_${width}`);
    if (height) transformations.push(`h_${height}`);
    if (width || height) transformations.push(`c_${crop}`);

    const transformString = transformations.join(',');
    
    return `https://res.cloudinary.com/${process.env.REACT_APP_CLOUDINARY_CLOUD_NAME}/image/upload/${transformString}/${publicId}`;
  }

  static getResponsiveUrls(publicId) {
    return {
      thumbnail: this.generateOptimizedUrl(publicId, { width: 150, height: 150 }),
      mobile: this.generateOptimizedUrl(publicId, { width: 400, height: 300 }),
      tablet: this.generateOptimizedUrl(publicId, { width: 800, height: 600 }),
      desktop: this.generateOptimizedUrl(publicId, { width: 1200, height: 900 }),
      original: this.generateOptimizedUrl(publicId)
    };
  }
}

// Composant pour l'upload d'images
export const ImageUploader = forwardRef(({
  onImagesChange, 
  maxImages = 5, 
  folder = 'listings',
  existingImages = [],
  className = ""
}, ref) => {
  const [uploading, setUploading] = useState(false);
  const [images, setImages] = useState(existingImages);
  const [dragActive, setDragActive] = useState(false);

  // Pour la caméra avec aperçu
  const [showCamera, setShowCamera] = useState(false);
  const [cameraStream, setCameraStream] = useState(null);
  const [currentFacingMode, setCurrentFacingMode] = useState('environment');
  const videoRef = React.useRef(null);

  // Détecter si on est sur mobile
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

  // Nettoyage du stream caméra
  React.useEffect(() => {
    return () => {
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
      }
    };
  }, [cameraStream]);

  const validateFile = (file) => {
    const maxSize = 10 * 1024 * 1024; // 10MB
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

    if (!allowedTypes.includes(file.type)) {
      return { valid: false, error: 'Type de fichier non autorisé. Utilisez JPEG, PNG, WebP ou GIF.' };
    }

    if (file.size > maxSize) {
      return { valid: false, error: 'Fichier trop volumineux. Maximum 10MB.' };
    }

    return { valid: true };
  };

  const uploadImages = useCallback(async (files) => {
    if (images.length + files.length > maxImages) {
      alert(`Maximum ${maxImages} images autorisées`);
      return;
    }

    setUploading(true);
    const newImages = [];

    try {
      for (const file of files) {
        const validation = validateFile(file);
        if (!validation.valid) {
          alert(`${file.name}: ${validation.error}`);
          continue;
        }

        const result = await CloudinaryUploadService.uploadDirect(file, { folder });
        
        if (result.success) {
          // Notre API backend retourne directement l'objet CloudinaryImageResponse
          newImages.push({
            public_id: result.data.public_id,
            url: result.data.original,
            thumbnail: result.data.thumbnail,
            mobile: result.data.mobile,
            tablet: result.data.tablet,
            desktop: result.data.desktop,
            original: result.data.original,
            width: result.data.width,
            height: result.data.height
          });
        } else {
          alert(`Erreur upload ${file.name}: ${result.error}`);
        }
      }

      const updatedImages = [...images, ...newImages];
      setImages(updatedImages);
      
      if (onImagesChange) {
        onImagesChange(updatedImages);
      }
    } catch (error) {
      console.error('Erreur lors de l\'upload:', error);
      alert('Erreur lors de l\'upload des images');
    } finally {
      setUploading(false);
    }
  }, [images, maxImages, folder, onImagesChange]);

  const handleFileSelect = useCallback((e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      uploadImages(files);
    }
  }, [images]);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      uploadImages(files);
    }
  }, [images]);

  const removeImage = useCallback((index) => {
    const updatedImages = images.filter((_, i) => i !== index);
    setImages(updatedImages);
    
    if (onImagesChange) {
      onImagesChange(updatedImages);
    }
  }, [images, onImagesChange]);

  const moveImage = useCallback((fromIndex, toIndex) => {
    const updatedImages = [...images];
    const [movedImage] = updatedImages.splice(fromIndex, 1);
    updatedImages.splice(toIndex, 0, movedImage);
    
    setImages(updatedImages);
    
    if (onImagesChange) {
      onImagesChange(updatedImages);
    }
  }, [images, onImagesChange]);

  // Ajoute cette méthode pour l'appel caméra
  const uploadFileFromCamera = async (file) => {
    await uploadImages([file]);
  };

  // Démarre la caméra avec aperçu
  const startCamera = async (facingMode) => {
    try {
      // Arrêter le stream précédent s'il existe
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { 
          facingMode: facingMode,
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      });
      
      setCameraStream(stream);
      setCurrentFacingMode(facingMode);
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      
    } catch (error) {
      console.error('Erreur accès caméra:', error);
      alert('Impossible d\'accéder à la caméra: ' + error.message);
      setShowCamera(false);
    }
  };

  // Ferme la caméra
  const closeCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }
    setShowCamera(false);
  };

  // Capture une photo depuis la vidéo
  const capturePhoto = () => {
    const video = videoRef.current;
    if (!video) return;
    
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    
    // Convertir en fichier
    canvas.toBlob(async (blob) => {
      const file = new File([blob], `photo_${Date.now()}.jpg`, { type: 'image/jpeg' });
      console.log('Photo capturée:', file.name, file.type);
      
      // Fermer la caméra
      closeCamera();
      
      // Upload et ajout à la liste
      await uploadFileFromCamera(file);
    }, 'image/jpeg', 0.9);
  };

  // Ouvre l'appareil photo avec aperçu
  const handleOpenCamera = () => {
    setShowCamera(true);
    startCamera('environment'); // Caméra arrière par défaut
  };

  // Expose les méthodes via le ref
  useImperativeHandle(ref, () => ({
    uploadFileFromCamera,
    handleOpenCamera
  }));

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Boutons d'action pour ajouter des images */}
      <div className="flex gap-3 mb-4">
        {/* Bouton pour choisir depuis l'appareil */}
        <label className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer flex items-center justify-center gap-2 text-sm font-medium">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-5l-2-2H5a2 2 0 00-2 2z" />
          </svg>
          📁 Choisir depuis l'appareil
          <input
            type="file"
            multiple
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
            disabled={uploading || images.length >= maxImages}
          />
        </label>
        
        {/* Bouton pour prendre une photo */}
        <button
          type="button"
          onClick={handleOpenCamera}
          disabled={uploading || images.length >= maxImages}
          className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm font-medium"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          📷 Prendre une photo
        </button>
      </div>
      {/* Interface caméra avec aperçu */}
      {showCamera && (
        <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center">
          <div className="relative w-full h-full max-w-md max-h-96 bg-black rounded-lg overflow-hidden">
            {/* Vidéo aperçu */}
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="w-full h-full object-cover"
            />
            
            {/* Contrôles caméra */}
            <div className="absolute bottom-4 left-0 right-0 flex items-center justify-center gap-4">
              {/* Bouton fermer */}
              <button
                onClick={closeCamera}
                className="p-3 rounded-full bg-gray-600 text-white hover:bg-gray-700"
                title="Fermer"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              
              {/* Bouton capture */}
              <button
                onClick={capturePhoto}
                disabled={uploading}
                className="p-4 rounded-full bg-white text-black hover:bg-gray-200 disabled:opacity-50"
                title="Prendre la photo"
              >
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              </button>
              
              {/* Bouton changer caméra (mobile seulement) */}
              {isMobile && (
                <button
                  onClick={() => startCamera(currentFacingMode === 'environment' ? 'user' : 'environment')}
                  className="p-3 rounded-full bg-gray-600 text-white hover:bg-gray-700"
                  title="Changer de caméra"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Zone d'upload par glisser-déposer */}
      <div
        className={`border-2 border-dashed rounded-lg p-6 text-center ${
          dragActive 
            ? 'border-blue-500 bg-blue-50' 
            : uploading
              ? 'border-gray-300 bg-gray-50'
              : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50'
        }`}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        style={{ minHeight: '120px' }}
      >
        {uploading ? (
          <div className="flex flex-col items-center">
            <svg className="w-8 h-8 animate-spin text-blue-600 mb-2" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <p className="text-blue-600">Upload en cours...</p>
          </div>
        ) : (
          <div>
            <svg className="w-12 h-12 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <p className="text-gray-600 mb-2">
              Ou glissez-déposez vos images ici
            </p>
            <p className="text-sm text-gray-500">
              PNG, JPG, WEBP jusqu'à {maxImages} images
            </p>
          </div>
        )}
      </div>

      {/* Aperçu des images */}
      {images.length > 0 && (
        <div className="space-y-2">
          <h4 className="font-medium text-gray-900">Images sélectionnées ({images.length}/{maxImages})</h4>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {images.map((image, index) => (
              <div key={image.public_id || index} className="relative group">
                <ImageWithPlaceholder
                  src={image.thumbnail || image.url}
                  alt={`Image ${index + 1}`}
                  className="w-full h-24 object-cover rounded-lg"
                />
                
                {/* Boutons d'action */}
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 rounded-lg transition-all duration-200 flex items-center justify-center">
                  <div className="opacity-0 group-hover:opacity-100 flex space-x-2">
                    {/* Bouton déplacer à gauche */}
                    {index > 0 && (
                      <button
                        onClick={() => moveImage(index, index - 1)}
                        className="p-1 bg-white rounded-full shadow-lg hover:bg-gray-100"
                        title="Déplacer à gauche"
                      >
                        <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                      </button>
                    )}
                    
                    {/* Bouton supprimer */}
                    <button
                      onClick={() => removeImage(index)}
                      className="p-1 bg-red-500 text-white rounded-full shadow-lg hover:bg-red-600"
                      title="Supprimer"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                    
                    {/* Bouton déplacer à droite */}
                    {index < images.length - 1 && (
                      <button
                        onClick={() => moveImage(index, index + 1)}
                        className="p-1 bg-white rounded-full shadow-lg hover:bg-gray-100"
                        title="Déplacer à droite"
                      >
                        <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
                
                {/* Indicateur d'image principale */}
                {index === 0 && (
                  <div className="absolute top-1 left-1 bg-blue-600 text-white text-xs px-2 py-1 rounded">
                    Principal
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
});

// Hook pour utiliser les images Cloudinary
export const useCloudinaryImages = (initialImages = []) => {
  const [images, setImages] = useState(initialImages);

  const addImages = (newImages) => {
    setImages(prev => [...prev, ...newImages]);
  };

  const removeImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  const moveImage = (fromIndex, toIndex) => {
    setImages(prev => {
      const updated = [...prev];
      const [movedImage] = updated.splice(fromIndex, 1);
      updated.splice(toIndex, 0, movedImage);
      return updated;
    });
  };

  const getOptimizedUrl = (image, options = {}) => {
    if (!image?.public_id) return image?.url || '';
    return CloudinaryUploadService.generateOptimizedUrl(image.public_id, options);
  };

  const getResponsiveUrls = (image) => {
    if (!image?.public_id) return {};
    return CloudinaryUploadService.getResponsiveUrls(image.public_id);
  };

  return {
    images,
    setImages,
    addImages,
    removeImage,
    moveImage,
    getOptimizedUrl,
    getResponsiveUrls
  };
};

export { CloudinaryUploadService };
